import React from "react";

import ChevronRightIcon from "@uui/ChevronRight";
import HashIcon from "@uui/Hash02";
import { find, first } from "lodash/fp";

import Typography from "@mui/material/Typography";

import useSlackConversations from "@app/analytics/hooks/integrations/useSlackConversations";
import useTeamsConversations from "@app/analytics/hooks/integrations/useTeamsConversations";
import type { SlackConversation } from "@app/analytics/types/slackTypes";
import type { TeamsConversation } from "@app/analytics/types/teamsTypes";
import type { UserDistributionSetting } from "@app/common/types";
import useAnchorElement from "@app/utils/hooks/useAnchorElement";

import { DropdownMenuItem } from "@rui/Dropdown";
import type { DropdownItem } from "@rui/Dropdown/types";
import { getSelectedFromOptions } from "@rui/Dropdown/utils";
import IconBackground from "@rui/IconBackground";
import { Row } from "@rui/Stack";
import TooltipWrapper from "@rui/TooltipWrapper";

import { ICON_OPTS_FOR_DISTRIBUTION_RECIPIENT } from "./constants";
import { SelectButton, SelectMenu } from "./styles";

type Conversation = SlackConversation | TeamsConversation;

const hasTeamId = (
  conversation: Conversation
): conversation is TeamsConversation => "teamId" in conversation;

const isSelectedConversation = (
  conversation: Conversation,
  setting: UserDistributionSetting
) => {
  if (setting.sendTo !== "custom") return false;
  if (hasTeamId(conversation)) {
    return (
      setting.providerChannelId === conversation.id &&
      (!setting.providerTeamId ||
        setting.providerTeamId === conversation.teamId)
    );
  }
  return setting.providerChannelId === conversation.id;
};

const OptionIcon = ({
  value,
}: {
  value: UserDistributionSetting["sendTo"];
}) => {
  if (value === "custom") {
    return (
      <IconBackground height={20} bgcolor="shades.meadow.50">
        <HashIcon sx={{ color: "shades.meadow.600" }} />
      </IconBackground>
    );
  }

  const opts = ICON_OPTS_FOR_DISTRIBUTION_RECIPIENT[value];
  if (!opts) return null;

  return (
    <IconBackground height={20} bgcolor={opts.bgcolor}>
      <opts.Icon sx={{ color: opts.color }} />
    </IconBackground>
  );
};

const ConversationSelectorMenu = ({
  conversations,
  label,
  onClose: onParentMenuClose,
  onSelect,
  setting,
}: {
  conversations: Conversation[];
  label: string;
  onClose: () => void;
  onSelect: (values: Partial<UserDistributionSetting>) => void;
  setting: UserDistributionSetting;
}) => {
  const { anchorElement, onClose, onClick, open } = useAnchorElement();

  const handleClose = () => {
    onClose();
    onParentMenuClose();
  };

  const handleSelect = (conversation: Conversation) => () => {
    onSelect({
      sendTo: "custom",
      providerChannelId: conversation.id,
      providerTeamId: hasTeamId(conversation) ? conversation.teamId : undefined,
    });
    handleClose();
  };

  return (
    <>
      <DropdownMenuItem
        onClick={onClick}
        label={
          <Row variant="apart" width="100%">
            <Typography variant="bodySecondaryReg" color="shades.neutral.600">
              {label}
            </Typography>
            <ChevronRightIcon
              sx={{ fontSize: "16px", color: "shades.neutral.600" }}
            />
          </Row>
        }
      />
      <SelectMenu
        anchorEl={anchorElement}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {conversations.map((conversation) => (
          <DropdownMenuItem
            key={conversation.id}
            onClick={handleSelect(conversation)}
            label={conversation.name}
            selected={isSelectedConversation(conversation, setting)}
          />
        ))}
      </SelectMenu>
    </>
  );
};

const DistributionRecipientsSelect = ({
  name,
  channel,
  defaultValue,
  onChange,
  options,
  setting,
}: {
  name: string;
  channel: UserDistributionSetting["method"];
  defaultValue?: UserDistributionSetting["sendTo"];
  onChange?: (updatedSetting: UserDistributionSetting) => void;
  options: DropdownItem<UserDistributionSetting["sendTo"]>[];
  setting?: UserDistributionSetting;
}) => {
  const { anchorElement, onClose, onClick, open } = useAnchorElement();

  const { conversations: slackConversations } = useSlackConversations({
    enabled: channel === "slack",
  });
  const { conversations: teamsConversations } = useTeamsConversations({
    enabled: channel === "teams",
  });

  if (!setting) return null;

  if (options.length < 2 || !onChange) {
    const selectedOption = first(options)!;
    return (
      <TooltipWrapper title="There are no other available options to choose from at this time.">
        <SelectButton
          label="Send to"
          disabled
          startIcon={<OptionIcon value={selectedOption.value} />}
          sx={{ minWidth: "240px" }}
          name={name}
        >
          {selectedOption.label}
        </SelectButton>
      </TooltipWrapper>
    );
  }

  const selectedValue = setting.sendTo;

  const handleSelectPreset =
    (value: UserDistributionSetting["sendTo"]) => () => {
      onChange({ ...setting, sendTo: value });
      onClose();
    };

  const handleSelectConversation = (
    partialSettings: Partial<UserDistributionSetting>
  ) => {
    onClose();
    onChange({ ...setting, ...partialSettings });
  };

  const selectedItemLabel =
    selectedValue === "custom"
      ? find(
          (c) => isSelectedConversation(c, setting),
          setting.method === "slack" ? slackConversations : teamsConversations
        )?.name
      : getSelectedFromOptions(options, selectedValue)?.label;

  return (
    <>
      <SelectButton
        label="Send to"
        startIcon={<OptionIcon value={selectedValue} />}
        sx={{ minWidth: "240px" }}
        name={name}
        onClick={onClick}
      >
        {selectedItemLabel ?? "-"}
      </SelectButton>
      <SelectMenu anchorEl={anchorElement} onClose={onClose} open={open}>
        {options.map((option) => {
          if (option.value === "custom") {
            const conversations =
              setting.method === "slack"
                ? slackConversations
                : teamsConversations;

            return (
              <ConversationSelectorMenu
                key={option.value}
                conversations={conversations}
                label={option.label!}
                onClose={onClose}
                onSelect={handleSelectConversation}
                setting={setting}
              />
            );
          }

          return (
            <DropdownMenuItem
              key={option.value}
              label={option.label}
              isDefault={option.value === defaultValue}
              onClick={handleSelectPreset(option.value)}
              selected={selectedValue === option.value}
              value={option.value}
            />
          );
        })}
      </SelectMenu>
    </>
  );
};

export default DistributionRecipientsSelect;
